import {Injectable, OnDestroy} from '@angular/core';
import {
  Observable,
  ReplaySubject,
  catchError,
  first,
  map,
  retry,
  switchMap,
  takeUntil,
  throwError,
  timeout,
} from 'rxjs';

import * as APIM from '@common/utils/dist/models/api-messages.js';
import {UtilsPL2 as U} from '@common/utils/dist/utils/utils-pl2.js';
import {
  ActiveAPIServiceStore,
  ActiveAPIService,
} from '@stores/active-api-service.store';

import {APIError} from '@utils/api-errors';
import {EventAPIService, SendEventInput} from '../event-api.service';
import {ServiceDecorator} from './service-decorator';
import {RouteStore} from '@stores/route.store';
import {
  SocketStatus,
  WebsocketStatusStore,
} from '@stores/websocket-status.store';
import {PublicEventInput} from '@services/events/query-events';

@Injectable()
export class ChangeActiveServiceDecorator
  implements EventAPIService, ServiceDecorator<EventAPIService>, OnDestroy
{
  private _service: EventAPIService;

  private readonly _retryDelay = 3000;
  private readonly _retryCount = 3;
  private readonly _timeout = 5000;

  private _destroyed$ = new ReplaySubject<boolean>();

  constructor(
    private activeServiceStore: ActiveAPIServiceStore,
    private routeStore: RouteStore,
    private websocketStatusStore: WebsocketStatusStore,
  ) {}

  ngOnDestroy() {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  setService(service: EventAPIService): void {
    this._service = service;
  }

  sendEvent(input: SendEventInput): Observable<string | void> {
    if (U.isEmpty(this._service)) {
      throw new Error('[ChangeActiveServiceDecorator]service not initialized');
    }

    if (input.eventType === APIM.EventIntent.UserDetailsRead) {
      return this._waitForConnection$().pipe(
        switchMap(() => {
          this.activeServiceStore.setState(ActiveAPIService.WebSocketService);
          return this._service.sendEvent(input);
        }),
      );
    }

    if (this._isUsingWebSocket() && !(<PublicEventInput>input).unauth) {
      return this._waitForConnection$().pipe(
        switchMap(() => this._service.sendEvent(input)),
        catchError((err: APIError.Error) => {
          console.debug('[ChangeActiveServiceDecorator]caught error', err);
          const standardizedError = APIError.buildError(err);
          if (standardizedError?.status === 504) {
            console.log('WebSocketService failed, falling back to EntryAPI');
            this.activeServiceStore.setState(ActiveAPIService.EntryAPIService);
            // retry with a new service
            console.debug(
              '[ChangeActiveServiceDecorator]retrying event',
              input,
            );
            return this._service.sendEvent(input);
          } else {
            return throwError(() => standardizedError);
          }
        }),
      );
    } else {
      return this._service.sendEvent(input);
    }
  }

  private _getActiveAPIService(): ActiveAPIService {
    const routeState = this.routeStore.state();
    if (routeState.ueg === '1') {
      console.debug('[APIServiceUtils]using entry gateway(set by query param)');
      this.activeServiceStore.setState(ActiveAPIService.EntryAPIService);
      return ActiveAPIService.EntryAPIService;
    }
    const apiState = this.activeServiceStore.state();
    return apiState;
  }

  private _waitForConnection$(): Observable<void> {
    return this.websocketStatusStore.state$.pipe(
      first(
        (status) =>
          status === SocketStatus.Opened || status === SocketStatus.Reconnected,
      ),
      map(() => void 0),
      timeout(this._timeout),
      retry({delay: this._retryDelay, count: this._retryCount}),
      takeUntil(this._destroyed$),
    );
  }

  private _isUsingWebSocket(): boolean {
    return this._getActiveAPIService() === ActiveAPIService.WebSocketService;
  }
}
